<template>
    <v-container fluid class="pa-6">
      <!-- Cabecera -->
      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <h1 class="headline font-weight-bold mb-1">Control de Máquinas</h1>
          <p class="subtitle-1 grey--text text--darken-1">Configuración y estado del parque de maquinaria</p>
        </v-col>
        <v-col cols="12" md="6" class="d-flex align-center justify-md-end">
          <v-btn
            color="primary"
            large
            depressed
            class="rounded-lg"
            @click="openDialog()"
          >
            <v-icon left>mdi-plus</v-icon>
            Nueva Máquina
          </v-btn>
        </v-col>
      </v-row>
  
      <!-- Tabla de Máquinas -->
      <v-card outlined class="rounded-xl mt-2">
        <v-card-title class="pa-4">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Buscar máquina..."
            outlined
            dense
            hide-details
            style="max-width: 300px"
            clearable
          ></v-text-field>
        </v-card-title>
  
        <v-data-table
          :headers="headers"
          :items="maquinas"
          :search="search"
          class="bg-transparent"
        >
          <!-- Estado -->
          <template v-slot:item.active="{ item }">
            <v-chip
              :color="item.active ? 'success' : 'error'"
              small
              dark
            >
              {{ item.active ? 'Operativa' : 'Anulada' }}
            </v-chip>
          </template>
  
          <!-- Fecha de creación -->
          <template v-slot:item.created_at="{ item }">
            <span class="body-2 grey--text text--darken-1">
              {{ formatDate(item.created_at) }}
            </span>
          </template>
  
          <!-- Acciones -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-end">
              <v-btn
                icon
                small
                color="primary"
                class="mr-2"
                @click="openDialog(item)"
                title="Editar"
              >
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
              
              <v-btn
                icon
                small
                :color="item.active ? 'error' : 'success'"
                @click="toggleMaquina(item)"
                :title="item.active ? 'Anular máquina' : 'Activar máquina'"
              >
                <v-icon>{{ item.active ? 'mdi-minus-circle-outline' : 'mdi-check-circle-outline' }}</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
  
      <!-- Modal Crear/Editar -->
      <v-dialog v-model="dialog" max-width="500px" persistent>
        <v-card class="rounded-xl pa-2">
          <v-card-title class="headline font-weight-bold pa-4">
            {{ isEditing ? 'Editar Máquina' : 'Nueva Máquina' }}
          </v-card-title>
  
          <v-card-text class="pa-6">
            <v-form ref="formRef" v-model="isFormValid">
              <v-row>
                <!-- SECCIÓN 1: ASIGNACIÓN DE EMPRESA -->
                <v-col cols="12" v-if="esRamos && !isEditing" class="pb-0">
                  <div class="subtitle-2 mb-2 grey--text text--darken-2 d-flex align-center">
                    <v-icon small class="mr-2">mdi-office-building</v-icon>
                    Asignación de Propiedad
                  </div>
                  
                  <v-autocomplete 
                    v-model="editedItem.id_empresa" 
                    :items="clientes" 
                    item-text="nombre" 
                    item-value="id_cliente"
                    label="Cliente Propietario" 
                    placeholder="Buscar cliente (dejar vacío para Ramos)..."
                    chips 
                    outlined 
                    dense
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    :hint="!editedItem.id_empresa ? 'Asignación predeterminada: Ramos' : ''"
                    persistent-hint
                  >
                    <template v-slot:selection="{ item }">
                      <v-chip color="primary" small dark label>
                        {{ item.nombre }}
                      </v-chip>
                    </template>
                  </v-autocomplete>

                  <!-- AVISO DE ASIGNACIÓN AUTOMÁTICA -->
                  <v-alert
                    v-if="!editedItem.id_empresa"
                    dense
                    text
                    type="info"
                    icon="mdi-information-outline"
                    class="mt-3 mb-6 text-caption rounded-lg"
                    border="left"
                  >
                    Si no seleccionas un cliente específico, la máquina se registrará bajo el nombre de <strong>Ramos</strong>.
                  </v-alert>
                </v-col>

                <v-col cols="12">
                  <v-divider v-if="esRamos && !isEditing" class="mb-6"></v-divider>
                  
                  <div class="subtitle-2 mb-2 grey--text text--darken-2 d-flex align-center">
                    <v-icon small class="mr-2">mdi-robot-industrial</v-icon>
                    Identificación del Activo
                  </div>
                  
                  <v-text-field
                    v-model="editedItem.nombre"
                    label="Nombre de la máquina"
                    outlined
                    placeholder="Ej: Prensa Hidráulica 01"
                    :rules="[v => !!v || 'El nombre es obligatorio']"
                    counter="150"
                    required
                    class="rounded-lg"
                  ></v-text-field>
                </v-col>

                <!-- SECCIÓN 2: ESTADO OPERATIVO -->
                <v-col cols="12">
                  <v-card 
                    flat 
                    outlined 
                    class="pa-4 rounded-lg" 
                    :color="editedItem.active ? 'green lighten-5' : 'grey lighten-4'"
                    style="border-style: dashed !important;"
                  >
                    <v-row align="center" no-gutters>
                      <v-col>
                        <div class="font-weight-bold" :class="editedItem.active ? 'green--text text--darken-3' : 'grey--text text--darken-3'">
                          {{ editedItem.active ? 'Activo para trabajos' : 'Inactivo temporalmente' }}
                        </div>
                        <div class="caption grey--text text--darken-1">
                          {{ editedItem.active ? 'La máquina aparecerá en los listados de mantenimiento.' : 'La máquina no se podrá seleccionar para nuevos partes.' }}
                        </div>
                      </v-col>
                      <v-col cols="auto">
                        <v-switch
                          v-model="editedItem.active"
                          color="success"
                          inset
                          hide-details
                          class="mt-0"
                        ></v-switch>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
  
          <v-divider class="mx-4"></v-divider>
  
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" @click="closeDialog">Cancelar</v-btn>
            <v-btn
              color="primary"
              depressed
              width="120"
              :loading="isSaving"
              :disabled="!isFormValid"
              @click="saveMaquina"
            >
              {{ isEditing ? 'Actualizar' : 'Guardar' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- Modal de error por nombre duplicado -->
      <v-dialog v-model="duplicateDialog" max-width="400px">
        <v-card class="pa-4 rounded-lg">
          <v-card-title class="headline font-weight-bold">
            Nombre duplicado
          </v-card-title>
  
          <v-card-text>
            Ya existe una máquina registrada con ese nombre.  
            Por favor, elige un nombre diferente.
          </v-card-text>
  
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" depressed @click="duplicateDialog = false">
              Entendido
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- Snackbar -->
      <v-snackbar 
        v-model="notify.show" 
        :color="notify.color" 
        :timeout="notify.timeout"
        top
      >
        <v-icon left dark>{{ notify.icon }}</v-icon>
        {{ notify.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="notify.show = false">Cerrar</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </template>
  
  <script>
  import axios from 'axios'
  import { mapState } from 'vuex';
  
  export default {
    name: 'ControlMaquinas',
    data() {
      return {
        // Notificaciones
        notify: {
          show: false,
          text: '',
          color: 'success',
          icon: 'mdi-check-circle',
          timeout: 3000
        },
        // UI
        search: '',
        dialog: false,
        isSaving: false,
        isFormValid: false,
        isEditing: false,
        duplicateDialog: false,
        // Tabla (V2 usa text/value)
        headers: [
          { text: 'ID', value: 'id', align: 'start', width: '80px' },
          { text: 'Nombre de Máquina', value: 'nombre', align: 'start' },
          { text: 'Estado', value: 'active', align: 'center', width: '150px' },
          { text: 'Fecha Registro', value: 'created_at', align: 'start', width: '200px' },
          { text: 'Acciones', value: 'actions', align: 'end', sortable: false },
        ],
        maquinas: [],
        // Modelos
        editedItem: {
          id: null,
          nombre: '',
          active: true,
          id_empresa: null,
        },
        defaultItem: {
          id: null,
          nombre: '',
          active: true
        }
      }
    },
    computed: {
      ...mapState(["esRamos", "clientes"]),
      // Obtenemos el ID de la empresa del localStorage
      idEmpresa() {
        return localStorage.getItem("clienteSeleccionado");
      }
    },
    mounted() {
      this.loadMaquinas();
    },
    methods: {
      showNotify(text, color = 'success') {
        this.notify.text = text;
        this.notify.color = color;
        if (color === 'error') this.notify.icon = 'mdi-alert-circle';
        else if (color === 'info') this.notify.icon = 'mdi-information';
        else this.notify.icon = 'mdi-check-circle';
        this.notify.show = true;
      },
      formatDate(dateString) {
        if (!dateString) return '-';
        return new Intl.DateTimeFormat('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).format(new Date(dateString));
      },
      async loadMaquinas() {
        // Evitamos la petición si no hay empresa seleccionada
        if (!this.idEmpresa) return;
  
        try {
          const res = await axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/maquinas`, {
            params: { id_empresa: this.idEmpresa } // Enviado como query param
          });
          this.maquinas = res.data.maquinas || res.data;
        } catch (error) {
          this.showNotify('Error al cargar la lista de máquinas', 'error');
        }
      },
      existsDuplicateName(nombre, id = null) {
        const lower = nombre.trim().toLowerCase();
        return this.maquinas.some(m => 
          m.nombre.trim().toLowerCase() === lower &&
          m.id !== id
        );
      },
      openDialog(item = null) {
        if (item) {
          this.isEditing = true;
          this.editedItem = JSON.parse(JSON.stringify(item));
        } else {
          this.isEditing = false;
          this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
        }
        this.dialog = true;
      },
      closeDialog() {
        this.dialog = false;
        if (this.$refs.formRef) this.$refs.formRef.resetValidation();
      },
      async saveMaquina() {
        if (!this.isFormValid) return;
        this.isSaving = true;
  
        try {
          if (this.existsDuplicateName(this.editedItem.nombre, this.editedItem.id)) {
            this.duplicateDialog = true;
            this.isSaving = false;
            return;
          }
  
          // Preparamos el objeto con los datos y el id_empresa
          const payload = {
            nombre: this.editedItem.nombre,
            active: this.editedItem.active,
            id_empresa: this.editedItem.id_empresa ? this.editedItem.id_empresa : this.idEmpresa,
          };
  
          if (this.isEditing) {
            await axios.put(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/maquinas/${this.editedItem.id}`, payload);
            this.showNotify('Máquina actualizada correctamente', 'success');
          } else {
            await axios.post(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/maquinas`, payload);
            this.showNotify('Máquina registrada con éxito', 'success');
          }
  
          await this.loadMaquinas();
          this.closeDialog();
        } catch (error) {
          this.showNotify('Error al procesar la máquina', 'error');
        } finally {
          this.isSaving = false;
        }
      },
      async toggleMaquina(item) {
        try {
          const nuevoEstado = !item.active;
          await axios.patch(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/maquinas/${item.id}/estado`, {
            active: nuevoEstado,
            id_empresa: this.idEmpresa // Enviamos id_empresa para validar propiedad en el backend
          });
          this.showNotify(nuevoEstado ? 'Máquina operativa' : 'Máquina anulada', 'info');
          await this.loadMaquinas();
        } catch (error) {
          this.showNotify('No se pudo cambiar el estado de la máquina', 'error');
        }
      }
    }
  }
  </script>
  
  <style scoped>
  .rounded-xl {
    border-radius: 24px !important;
  }
  .rounded-lg {
    border-radius: 12px !important;
  }
  
  ::v-deep .v-data-table-header th {
    font-weight: bold !important;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.5px;
    color: #757575 !important;
  }
  
  ::v-deep .v-data-table__wrapper tr:hover {
    background-color: rgba(0, 0, 0, 0.02) !important;
  }
  </style>